import { Component, Input, Output, EventEmitter, ViewChild, TemplateRef, OnInit, OnDestroy } from '@angular/core';
import { WebSocketsService } from '../../../services/web-sockets.service';
import { Subscription } from 'rxjs';
import { SapiService, SapiEntity } from '../../../services/sapi.service';
import { TableRow, BbTableService } from '../../../services/bb-table.service';
import { NoticeDefinition, OverlayService } from '../../../services/overlay.service';
import { TranslateService } from '@ngx-translate/core';


export interface CloningEvent {
	ev: 'start' | 'update' | 'finish';
	status: 'started' | 'running' | 'finished';
	payload: {
		migrationJob?: MigrationJob;
		msg?: string;
		entity?: MigrationJobEntity;
	};
	Object: any;
}

export interface MigrationJob extends SapiEntity {
	publicationid?: string;
	otype: string;
	oid: string;
	destinationUrl: string;
}

export interface MigrationJobEntity {
	entityType: string;
	entityId: string;
	parentType?: string;
	parentId?: string;
	newEntityId: string;
	newParentId: string;
}

@Component({
	selector: 'bb-cloning-info',
	templateUrl: './bb-cloning-info.component.html',
	styleUrls: ['./bb-cloning-info.component.scss'],
	providers: [BbTableService]
})
export class BbCloningInfoComponent implements OnInit, OnDestroy {
	// Shows information about MigrationJobs
	@Input() selectedRows: any;
	@Output() onConfirm = new EventEmitter();
	@ViewChild('cloningInfoTemplate') cloningInfoTemplate: TemplateRef<any>;

	entitiesToClone: any;
	confirmed = false;
	migrationJobs: any = [];
	jobEvents: any = {};
	subscriptions = new Subscription();

	destinationUrl = 'PUBLICATION.bbvms.com';

	constructor(
		private webSocketsService: WebSocketsService,
		private overlayService: OverlayService,
		private sapiService: SapiService,
		private translate: TranslateService
	) {

	}

	ngOnInit() {
		this.sapiService.query('migrationjob', [], 0, 5).subscribe((data) => {
			data.items.forEach((mj) => {
				this.jobEvents[mj.id] = mj;
				this.migrationJobs.push(mj);
			});
		});
		this.subscriptions.add(this.webSocketsService.subscribeToChannel('cloning').subscribe((socketEvent: CloningEvent) => {
			this.onSocketEvent(socketEvent);
		}));
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
		this.webSocketsService.unsubscribeFromChannel('cloning');
	}

	cloneEntities(entitiesToClone: any[], vhost: string) {
		if (!entitiesToClone) {
			return;
		}
		let req: any;
		if (entitiesToClone.length > 1) {
			// multi
			req = {
				type: 'MigrationJobList',
				items: []
			};

			if (entitiesToClone && entitiesToClone.length) {
				entitiesToClone.forEach((et) => {
					req.items.push({
						oid: et.id,
						otype: et.type.toLowerCase(),
						destinationUrl: vhost
					});
				});
			}
		} else if (entitiesToClone.length === 1) {
			req = {
				oid: entitiesToClone[0].id,
				otype: entitiesToClone[0].type.toLowerCase(),
				destinationUrl: vhost
			} as MigrationJob;
		} else {
			return;
		}

		this.sapiService.create('migrationjob', req, true).subscribe((migrationJob: any) => {
			if (migrationJob && migrationJob.id) {
				// nothing yet
			}
		}, err => {
			const notice: NoticeDefinition = {
				icon: 'warning',
				title: this.translate.instant('PUBLICATION_NOT_FOUND'),
				type: 'error',
				contents: this.translate.instant('PUBLICATION_NOT_FOUND_INFO', { publication: req.destinationUrl })
			};
			this.overlayService.createNotice(notice);
		});
		this.entitiesToClone = null;
	}

	onSocketEvent(socketEvent: CloningEvent) {
		console.warn('socketEvent', socketEvent);
		if (socketEvent && socketEvent.payload && socketEvent.payload.migrationJob && socketEvent.payload.migrationJob.id) {
			let je = this.jobEvents[socketEvent.payload.migrationJob.id];
			if (!je) {
				je = this.jobEvents[socketEvent.payload.migrationJob.id] = socketEvent.payload.migrationJob;
				je.state = 'queued';
				je.opened = true;
				this.migrationJobs.unshift(je);
			}
			if (!je.messages) {
				je.messages = [];
			}
			je.messages.push(socketEvent);
			je.state = socketEvent.status;
		}
	}

	log(a: any) {
		console.warn('log: ', a);
	}

	getEntityLink(jobEntity: MigrationJobEntity, publicationVhost?: string) {
		if (!jobEntity) {
			return '';
		}
		const url = publicationVhost ? 'https://' + publicationVhost + '/ovp/' : '';
		let hash = '';

		const type = jobEntity.entityType;
		const newParentId = jobEntity.newParentId;

		const idToUse = publicationVhost ? jobEntity.newEntityId : jobEntity.entityId;

		switch (type) {
			case 'app':
				hash = '#/library/template/' + idToUse;
				break;
			case 'project':
				hash = '#/library/project/' + idToUse;
				break;
			case 'mediaclip':
				hash = '#/library/mediaclip/' + idToUse;
				break;
			case 'asset':
			case 'mediaasset':
				hash = '#/library/mediaclip/' + newParentId + '/resources';
				break;
			case 'subtitle':
				hash = '#/library/mediaclip/' + newParentId + '/languages';
				break;
			case 'timeline':
				hash = '#/library/interactivity/' + idToUse;
				break;
		}

		return url + hash;
	}

	cloneClicked(selectedRows: TableRow[]) {
		// console.log('clone selectedRows: ', selectedRows);
		if (!selectedRows || !selectedRows.length) {
			return;
		}
		const entitiesToClone: any[] = [];
		selectedRows.forEach((row) => {
			entitiesToClone.push(row.data);
		});
		this.openClonePane();
		this.entitiesToClone = entitiesToClone;
	}

	openClonePane() {
		const cloningInfoModal: any = {
			title: 'Cloning panel',
			component: {
				name: 'custom-content',
				templateRef: this.cloningInfoTemplate
			}
		};
		this.overlayService.createModal(cloningInfoModal);
	}

	close() {

	}
}
